///
/// EarthBrowser
///
/// Tile Token Service - Manages tile server authentication tokens
///
/// Created by Matt Giger on 1/10/2026
/// Copyright (c) 2026 Geodata Labs
///

const apiUrl = import.meta.env.VITE_API_URL || 'https://api.earthbrowser.com'

// Refresh token 5 minutes before expiration
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000

// After a failed token fetch (network error, rate limited), don't retry for
// this long. Anonymous visitors get tokens too, so failures are transient.
const TOKEN_RETRY_COOLDOWN_MS = 60 * 1000

interface TileTokenResponse {
	token: string
	expiresAt: string
}

/**
 * TileTokenService manages authentication tokens for tile server requests.
 * Tokens are stored in memory only (not persisted) and automatically refreshed.
 *
 * The API mints tokens for anonymous visitors too (tier 'anon'); signed-in
 * users get a token carrying their subscription tier. The token must be
 * cleared on sign-in/sign-out so the tier stays in sync with the session.
 */
class TileTokenService {
	private token: string | null = null
	private expiresAt: Date | null = null
	private refreshPromise: Promise<string | null> | null = null
	private refreshTimer: ReturnType<typeof setTimeout> | null = null
	private lastFailureAt = 0

	/**
	 * Get a valid tile token, refreshing if necessary
	 * Returns null only if the token fetch failed (network error, rate limit)
	 */
	async getToken(): Promise<string | null> {
		// If we have a valid token, return it
		if (this.isTokenValid()) {
			return this.token
		}

		// If a refresh is already in progress, wait for it
		if (this.refreshPromise) {
			return this.refreshPromise
		}

		// Recently failed (e.g. signed out) - don't hammer the API server
		if (Date.now() - this.lastFailureAt < TOKEN_RETRY_COOLDOWN_MS) {
			return null
		}

		// Fetch a new token
		return this.refreshToken()
	}

	/**
	 * Check if the current token is valid (exists and not expired/expiring soon)
	 */
	private isTokenValid(): boolean {
		if (!this.token || !this.expiresAt) {
			return false
		}

		// Consider token invalid if it expires within the buffer period
		const now = Date.now()
		const expiresIn = this.expiresAt.getTime() - now
		return expiresIn > TOKEN_REFRESH_BUFFER_MS
	}

	/**
	 * Fetch a new tile token from the API
	 */
	async refreshToken(): Promise<string | null> {
		// Prevent concurrent refresh requests
		if (this.refreshPromise) {
			return this.refreshPromise
		}

		this.refreshPromise = this.fetchToken()

		try {
			const token = await this.refreshPromise
			return token
		} finally {
			this.refreshPromise = null
		}
	}

	/**
	 * Internal method to fetch token from API
	 */
	private async fetchToken(): Promise<string | null> {
		try {
			const response = await fetch(`${apiUrl}/api/tiles/token`, {
				method: 'GET',
				credentials: 'include'
			})

			if (!response.ok) {
				if (response.status === 401) {
					// Older API deployments only minted tokens for signed-in
					// users - clear any existing token and back off
					this.clearToken()
					this.lastFailureAt = Date.now()
					return null
				}
				// Includes 429 when anonymous minting is rate limited
				throw new Error(`Failed to fetch tile token: ${response.statusText}`)
			}

			const data: TileTokenResponse = await response.json()

			this.token = data.token
			this.expiresAt = new Date(data.expiresAt)

			// Schedule automatic refresh before expiration
			this.scheduleRefresh()

			return this.token
		} catch (error) {
			console.error('[TileTokenService] Failed to fetch tile token:', error)
			this.lastFailureAt = Date.now()
			return null
		}
	}

	/**
	 * Schedule automatic token refresh before expiration
	 */
	private scheduleRefresh(): void {
		// Clear any existing timer
		if (this.refreshTimer) {
			clearTimeout(this.refreshTimer)
			this.refreshTimer = null
		}

		if (!this.expiresAt) {
			return
		}

		// Calculate when to refresh (5 minutes before expiration)
		const now = Date.now()
		const expiresIn = this.expiresAt.getTime() - now
		const refreshIn = Math.max(0, expiresIn - TOKEN_REFRESH_BUFFER_MS)

		// Only schedule if token has significant time remaining
		if (refreshIn > 0) {
			this.refreshTimer = setTimeout(() => {
				this.refreshToken()
			}, refreshIn)
		}
	}

	/**
	 * Clear the current token (e.g., on sign out)
	 */
	clearToken(): void {
		this.token = null
		this.expiresAt = null
		this.lastFailureAt = 0

		if (this.refreshTimer) {
			clearTimeout(this.refreshTimer)
			this.refreshTimer = null
		}
	}

	/**
	 * Check if a token is currently available
	 * This is a quick check that doesn't trigger a refresh
	 */
	hasToken(): boolean {
		return this.token !== null && this.isTokenValid()
	}
}

// Singleton instance
let tileTokenServiceInstance: TileTokenService | null = null

/**
 * Get the singleton TileTokenService instance
 */
export function getTileTokenService(): TileTokenService {
	if (!tileTokenServiceInstance) {
		tileTokenServiceInstance = new TileTokenService()
	}
	return tileTokenServiceInstance
}

/**
 * Convenience function to get a tile token
 * Returns null only if the token fetch failed (network error, rate limit)
 */
export async function getTileToken(): Promise<string | null> {
	return getTileTokenService().getToken()
}

/**
 * Clear tile token (call on sign out and sign in, so the token tier
 * follows the session)
 */
export function clearTileToken(): void {
	getTileTokenService().clearToken()
}
